// ============================================
// MA-800 — one cloud `students` row must never serve two local children.
//
// THE HOLE: the three lookup-by-name paths (onboarding reuse in App.jsx
// handleOnboardingComplete, childrenStore.autoLinkByNameAge, and the lazy-create
// lookup in useActiveStudentId.resolveCanonicalStudentId) accepted a same-name
// cloud row even when the registry ALREADY linked it to a DIFFERENT local child.
// A second kid with a name already on the device then shared the first kid's
// row: attempts, milestones, sessions and every cloud field leaked across them.
//
// THE RULE: a cloud row is only a link candidate for local child X when no OTHER
// local child's supabaseStudentId points at it. A same-name row that NO local
// child holds is still reused (CLAUDE.md "Student-create discipline": that reuse
// is what prevents the 22-duplicates bug). With the exclusion, "add a kid whose
// name matches an existing kid" finds no available row and falls to
// createCloudStudent -> a distinct row.
//
// Pure: no storage, no network. Callers pass the registry they read.
// ============================================

const norm = (s) => String(s || "").trim().toLowerCase();

/** Cloud rows that are NOT linked to any local child other than `forLocalChildId`
 *  (pass null/undefined for a child that does not exist yet, e.g. onboarding). */
export function cloudRowsAvailableForLink(registry, cloudStudents, forLocalChildId = null) {
  const rows = Array.isArray(cloudStudents) ? cloudStudents.filter((s) => s && s.id) : [];
  const children = registry && Array.isArray(registry.children) ? registry.children : [];
  const taken = new Set();
  for (const c of children) {
    if (c && c.supabaseStudentId && c.id !== forLocalChildId) taken.add(c.supabaseStudentId);
  }
  return rows.filter((s) => !taken.has(s.id));
}

/** Production forbids two same-name kids per parent (students_parent_name_unique,
 *  migration 0013: parent_id + lower(name); createStudent then returns the EXISTING
 *  row). So a same-name add must be refused up front. Compares lowercased + trimmed.
 *  Only the signed-in user's children count (owner match, or legacy unowned);
 *  kiosk identities never do. `editingChildId` excludes the kid being edited. */
export function nameTakenOnDevice(children, name, userId, editingChildId = null) {
  const n = norm(name);
  if (!n) return false;
  const list = Array.isArray(children) ? children : [];
  return list.some(
    (c) =>
      c && !c.kioskEnrollmentId && c.id !== editingChildId &&
      (!userId || !c.ownerUserId || c.ownerUserId === userId) &&
      norm(c.name) === n,
  );
}

/** The name as the name step stores/shows it: trimmed, first letter upper-cased. */
export const displayKidName = (name) => String(name || "").trim().replace(/^./, (c) => c.toUpperCase());

/** The exact parent-facing copy for a refused same-name add. */
export const sameNameCopy = (name) =>
  `You already have a kid named ${displayKidName(name)}. Add a last initial or a nickname so we can tell them apart.`;

/** Name-step / Settings add decision. {ok:true} or {ok:false, message}. */
export function checkKidName({ children, name, userId, editingChildId = null }) {
  return nameTakenOnDevice(children, name, userId, editingChildId)
    ? { ok: false, message: sameNameCopy(name) }
    : { ok: true };
}

/** Local children (other than `exceptChildId`) already linked to `rowId`. */
export function childrenHoldingRow(registry, rowId, exceptChildId = null) {
  const children = registry && Array.isArray(registry.children) ? registry.children : [];
  return children.filter((c) => c && rowId && c.supabaseStudentId === rowId && c.id !== exceptChildId).map((c) => c.id);
}

/** Sentry context (ids only) for a refused link / held create result. */
export function heldRowPayload(registry, rowId, exceptChildId = null) {
  const ids = childrenHoldingRow(registry, rowId, exceptChildId);
  return ids.length ? { groups: [{ supabaseStudentId: rowId, childIds: ids }] } : null;
}

const sameName = (rows, name) => rows.filter((s) => s && s.id && norm(s.name) && norm(s.name) === norm(name));

/** Onboarding / Add-a-kid reuse decision. Returns the one row to reuse, else
 *  null (caller creates a fresh row). The exclusion only ever REMOVES options:
 *   - ambiguity is decided over ALL same-name cloud rows (taken or not), as it
 *     was before MA-800: more than one -> create (a leftover duplicate must not
 *     become "the unambiguous row" just because its twin is taken);
 *   - the single same-name row must also be free (not linked to a local child);
 *   - a local child already carrying this name (kiosk identities excluded)
 *     means the parent is adding ANOTHER kid -> create. Re-onboarding a kid
 *     whose local entry is gone (the 22-duplicates guard) has no such child. */
export function pickOnboardingReuseRow(registry, cloudStudents, name, userId = null) {
  const children = registry && Array.isArray(registry.children) ? registry.children : [];
  if (nameTakenOnDevice(children, name, userId)) return null;
  const all = sameName(Array.isArray(cloudStudents) ? cloudStudents : [], name);
  if (all.length !== 1) return null;
  const free = cloudRowsAvailableForLink(registry, cloudStudents, null);
  return free.some((s) => s.id === all[0].id) ? all[0] : null;
}

/** Shared name(+age) picker for an EXISTING unlinked local child (lazy-create
 *  and autoLinkByNameAge). Ambiguity is decided over all same-name rows not in
 *  `claimed`; a lone same-name row is taken only when free; with several, only
 *  a free row whose age matches the child's is taken (no age -> none). */
export function pickLinkRowForChild(registry, cloudStudents, child, claimed = null) {
  if (!child) return null;
  // Ambiguity counts ALL same-name rows (names compared lowercased + trimmed),
  // INCLUDING ones claimed earlier in the same auto-link pass; claimed rows are
  // just never picked.
  const pool = sameName(Array.isArray(cloudStudents) ? cloudStudents : [], child.name);
  if (pool.length === 0) return null;
  const free = new Set(cloudRowsAvailableForLink(registry, cloudStudents, child.id).map((s) => s.id));
  const pickable = (s) => free.has(s.id) && !(claimed && claimed.has(s.id));
  if (pool.length === 1) return pickable(pool[0]) ? pool[0] : null;
  if (child.age == null) return null;
  return pool.find((s) => pickable(s) && Number(s.age) === Number(child.age)) || null;
}

/** Lazy-create lookup (resolveCanonicalStudentId). */
export function pickLazyReuseRow(registry, cloudStudents, activeChild) {
  return pickLinkRowForChild(registry, cloudStudents, activeChild, null);
}

/** Detector for registries already damaged by the bug: groups of local child
 *  ids (registry order) that share one supabaseStudentId. Pure; never mutates. */
export function findDoubleLinkedChildren(registry) {
  const children = registry && Array.isArray(registry.children) ? registry.children : [];
  const by = new Map();
  for (const c of children) {
    if (!c || !c.id || !c.supabaseStudentId) continue;
    const arr = by.get(c.supabaseStudentId) || [];
    arr.push(c.id);
    by.set(c.supabaseStudentId, arr);
  }
  return [...by.entries()]
    .filter(([, ids]) => ids.length > 1)
    .map(([supabaseStudentId, childIds]) => ({ supabaseStudentId, childIds }));
}

/** Stable key for a detection result, so a caller reports each distinct damage once. */
export function doubleLinkSignature(groups) {
  return groups.map((g) => `${g.supabaseStudentId}:${g.childIds.join(",")}`).sort().join("|");
}

/** Boot-time report for already-damaged registries. Returns the Sentry context
 *  payload `{ groups: [{ supabaseStudentId, childIds }] }` the FIRST time a
 *  distinct damage is seen, else null (nothing wrong, or already reported).
 *  `seen` is a Set the caller keeps for the session. The payload carries ONLY
 *  the cloud row UUID and local ids — never a child's name or age (COPPA). */
export function doubleLinkReport(registry, seen) {
  const groups = findDoubleLinkedChildren(registry).map((g) => ({
    supabaseStudentId: g.supabaseStudentId,
    childIds: [...g.childIds],
  }));
  if (groups.length === 0) return null;
  const sig = doubleLinkSignature(groups);
  if (seen.has(sig)) return null;
  seen.add(sig);
  return { groups };
}
